<template>
  <div
    v-if="averages !== null"
    class="insights-benchmarks-averages"
    :class="{ 'has-multi-pin': isMultiPin, 'pb-20': isMultiPin }"
  >
    <div
      v-if="!isLoading"
      class="wrapper"
    >
      <span class="legend-percentage">{{ minLabel }}</span>
      <section
        class="layout"
        :class="{ 'layout-empty': !computedAverages.length }"
      >
        <div
          v-for="bar in computedAverages"
          :key="bar.key"
          v-tooltip="tooltipOptions"
          class="bar"
          :class="getBarClass(bar)"
          :style="getBarStyles(bar)"
          :title="bar.tooltipContent"
        ></div>

        <template v-if="computedAverages.length > 0">
          <div
            v-for="(row, rowIndex) in normalizedPins"
            :key="`pin-row-${row.key}`"
            class="dual-pin-row"
          >
            <div
              v-for="(pinValue, pinIndex) in row.values"
              :key="`pin-${row.key}-${pinIndex}`"
              class="value-pointer-wrapper value-pointer-wrapper-wide"
              :style="getPinWrapperStyles(rowIndex, pinValue)"
            >
              <div
                class="value"
                :class="{ 'visibility-hidden': !Number.isFinite(pinValue) }"
                :style="getPinValueStyles(pinIndex, row.values)"
              >
                <span
                  v-if="isMultiPin"
                  :class="getPinClass(row.key)"
                ></span>{{ valueFormatter(pinValue) }}
              </div>
              <div class="pointer"></div>
              <img
                v-if="!isMultiPin"
                :src="ICON_TRIANGLE_ROUNDED"
              />
            </div>
          </div>
        </template>

        <div
          v-if="!computedAverages.length"
          class="layout-empty-text text-center"
        >
          Not enough data
        </div>
      </section>
      <span class="legend-percentage">{{ maxLabel }}</span>
    </div>
    <div
      v-else
      class="wrapper-loading"
    ></div>
  </div>
  <div
    v-else
    class="insights-benchmarks-averages-placeholder"
  >
    <img :src="ICON_GEARS" />
    <span>Benchmark coming soon</span>
  </div>
</template>

<script>
import { INSIGHTS } from '~/app_constants';
import { ICON_GEARS, ICON_TRIANGLE_ROUNDED } from '~/rails_assets';
import { InsightsTracking } from '~/mixins';

export default {
  name: 'InsightsBenchmarksAverages',
  mixins: [InsightsTracking],
  props: {
    /**
     * Whether the component is in loading state
     */
    isLoading: {
      type: Boolean,
      default: true,
    },
    /**
     * Benchmark averages data object. Keys are benchmark types (e.g., 'organization', 'occupation', 'remote', 'on_site').
     * Each value should have { range: [min, max], name: string, warning?: string }.
     * Pass null to show "Benchmark coming soon" placeholder.
     */
    averages: {
      type: Object,
      default: null,
    },
    /**
     * Array of pin markers to display on the scale.
     * Each pin: { key: string, value: number | number[] }
     * - key: identifier for styling (e.g., 'remote', 'on_site', 'default')
     * - value: single number or array of numbers (for range pins like start/stop times)
     *
     * Examples:
     * - Single pin: [{ key: 'standard', value: 75 }]
     * - Multiple pins: [{ key: 'remote', value: 80 }, { key: 'on_site', value: 65 }]
     * - Range pins: [{ key: 'remote', value: [480, 1020] }] (for start/end times)
     */
    pins: {
      type: Array,
      default: () => [],
      validator: (pins) => pins.every((pin) =>
        typeof pin === 'object' &&
        typeof pin.key === 'string' &&
        (typeof pin.value === 'number' || Array.isArray(pin.value)),
      ),
    },
    /**
     * Minimum value of the scale
     */
    min: {
      type: Number,
      default: 0,
    },
    /**
     * Maximum value of the scale
     */
    max: {
      type: Number,
      default: 100,
    },
    /**
     * Function to format pin values for display
     */
    valueFormatter: {
      type: Function,
      default: (v) => `${v.toFixed(0)}%`,
    },
    /**
     * Widget name for analytics tracking
     */
    widgetName: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    tooltipOptions: Object.freeze({
      html: true,
      sanitize: false,
      template: INSIGHTS.tooltips.benchmarksAverages,
    }),
    ICON_GEARS,
    ICON_TRIANGLE_ROUNDED,
  }),
  computed: {
    /**
     * Whether to show RVO (Remote vs Office) style with colored indicators.
     * True when any pin has a location-specific key (remote/on_site).
     */
    isMultiPin() {
      const rvoKeys = ['remote', 'on_site'];

      return this.pins.some((pin) => rvoKeys.includes(pin.key));
    },
    /**
     * Normalized pins structure for unified rendering.
     * Transforms pins array into consistent format: [{ key, values: number[] }]
     */
    normalizedPins() {
      return this.pins.map((pin) => ({
        key: pin.key,
        values: Array.isArray(pin.value) ? pin.value : [pin.value],
      }));
    },
    computedAverages() {
      if (Object.entries(this.averages).length === 0) {
        return [];
      }

      return Object.entries(this.averages)
        .filter(
          ([_, value]) =>
            value !== null &&
            value.range &&
            value.range.length === 2 &&
            value.range[0] < this.max &&
            value.range[1] <= this.max,
        )
        .map(([key, value]) => {
          return {
            ...value,
            key,
            label: INSIGHTS.benchmarks_averages.labels[key],
            tooltipContent: this.buildTooltipContent({ key, ...value }),
          };
        });
    },
    minLabel() {
      return this.valueFormatter(this.min);
    },
    maxLabel() {
      return this.valueFormatter(this.max);
    },
  },
  methods: {
    buildTooltipContent(benchmark) {
      let benchmarkTypeCopy;
      let warningCopy;
      const isEmptyRange = !!benchmark.range.length;
      const rangeCopy = () => isEmptyRange
        ? ` range: ${Math.round(benchmark.range[0])}% - ${Math.round(benchmark.range[1])}%`
        : '';

      if (['organization', 'occupation'].includes(benchmark.key)) {
        benchmarkTypeCopy = `${benchmark.name}'s average`;
      } else if (benchmark.key === 'remote') {
        benchmarkTypeCopy = 'Remote average';
      } else if (benchmark.key === 'on_site') {
        benchmarkTypeCopy = 'Office average';
      } else {
        benchmarkTypeCopy = `${benchmark.name} Industry average`;
      }

      if (benchmark.warning) {
        warningCopy = INSIGHTS.benchmarks_averages.warnings.widget[benchmark.warning];
      }

      return `<div>${benchmarkTypeCopy} ${rangeCopy()}</div>${this.handleWarningCopy(warningCopy)}`;
    },
    getBarStyles(bar) {
      const range = this.max - this.min;

      return {
        width: `${Math.round((bar.range[1] - bar.range[0]) / range * 100)}%`,
        marginLeft: `${Math.round((bar.range[0] - this.min) / range * 100)}%`,
      };
    },
    handleWarningCopy(warningCopy) {
      return warningCopy
        ? `<div><i class="hi hi-warning mr-5 insights-benchmarks-averages-warning-icon" />${warningCopy}</div>`
        : '';
    },
    getBarClass(bar) {
      return {
        'bar-blurred': bar.warning,
        [`insights-benchmarks-averages-color-${bar.key}`]: true,
      };
    },
    getPinClass(barKey) {
      return {
        'insights-benchmarks-pins-color': true,
        [`insights-benchmarks-pins-color-${barKey}`]: true,
      };
    },
    getPinWrapperStyles(rowIndex, value) {
      const range = this.max - this.min;
      // Clamp value to [min, max] range and calculate percentage
      const clampedValue = Math.max(this.min, Math.min(this.max, value));
      const percent = ((clampedValue - this.min) / range) * 100;

      return {
        left: `${percent}%`,
        top: rowIndex === 0 ? '-20px' : '10px',
        flexDirection: rowIndex === 0 ? 'column' : 'column-reverse',
        transform: 'translateX(-50%)',
      };
    },
    getPinValueStyles(pinIndex, pins) {
      if (pins.length < 2) {
        return {};
      }

      // Distance between pins as a fraction of the scale (0..1)
      const range = this.max - this.min;
      const distanceRatio = Math.abs(pins[1] - pins[0]) / range;

      // Estimated label width as fraction of the scale
      const labelWidthRatio = 0.25;

      // No shift needed if labels don't overlap
      if (distanceRatio >= labelWidthRatio) {
        return {};
      }

      // Proportional shift: 0% when labels just touch, 50% when pins overlap completely
      const shiftPercent = (1 - distanceRatio / labelWidthRatio) * 50;
      const sign = pinIndex === 0 ? -1 : 1;

      return { transform: `translateX(${sign * shiftPercent}%)` };
    },
  },
};
</script>
